// src/components/gallery/ctl-gallery.js
//
// ctl-gallery: responsive gallery grid with optional selection and lightbox.
//
// Attributes
// - columns (string)          : "auto" | "2" | "3" | "4" | "5" | "6" (default "auto")
// - gap (string)              : "xs" | "sm" | "md" | "lg" | "xl" (default "md")
// - aspect (string)           : "auto" | "1:1" | "4:3" | "16:9" (default "auto")
// - selectable (boolean)      : allow selecting cards
// - multiple (boolean)        : allow multi-select (requires selectable)
// - value (string)            : selected id (single mode)
// - values (json array)       : selected ids (multi mode) e.g. '["a","b"]'
// - lightbox (boolean)        : enable built-in lightbox viewer (default false)
// - disabled (boolean)
//
// Properties
// - items (array)             : [{ id, src, alt?, title?, subtitle?, href?, disabled?, meta? }]
//
// Events
// - ctl-select   (detail: { id, item, selected, values })
// - ctl-open     (detail: { id, item })   // when opening lightbox
// - ctl-navigate (detail: { id, href, item }) // when item has href and clicked (lightbox off)
// - ctl-action   (detail: { id, action, item }) // optional: future hook (not used here)
//
// Slots
// - item (template) optional: custom card template
//   <template slot="item"> ... {{title}} {{subtitle}} {{src}} {{alt}} {{id}} {{selected}} ... </template>
// - empty : empty state content
//
// Notes
// - Uses roving tabindex for selectable mode.
// - Arrow keys navigate grid (best-effort).
// - Enter/Space selects (or opens lightbox if enabled and not selectable).
// - Escape closes lightbox.

import "../layouts/ctl-card.js";
import "../foundation/ctl-badge.js";
import "./ctl-tag.js";
import "../foundation/ctl-button.js";
import "../foundation/ctl-divider.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-text: var(--ctl-color-text);
    --ctl-muted: var(--ctl-color-muted);
    --ctl-bg: var(--ctl-color-bg);
    --ctl-surface: var(--ctl-color-surface);
    --ctl-border: var(--ctl-color-border);
    --ctl-brand: var(--ctl-color-brand);
    --ctl-focus: var(--ctl-focus-ring, 0 0 0 3px rgba(99,102,241,.15));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-text);
  }

  .grid{
    display:grid;
    gap: var(--gap, var(--ctl-space-3));
  }

  /* columns */
  :host([columns="2"]) .grid{ grid-template-columns: repeat(2, minmax(0,1fr)); }
  :host([columns="3"]) .grid{ grid-template-columns: repeat(3, minmax(0,1fr)); }
  :host([columns="4"]) .grid{ grid-template-columns: repeat(4, minmax(0,1fr)); }
  :host([columns="5"]) .grid{ grid-template-columns: repeat(5, minmax(0,1fr)); }
  :host([columns="6"]) .grid{ grid-template-columns: repeat(6, minmax(0,1fr)); }

  /* auto columns */
  :host([columns="auto"]) .grid{
    grid-template-columns: repeat(auto-fill, minmax(calc(var(--ctl-space-7) * 3.75), 1fr));
  }
  :host(:not([columns])) .grid{
    grid-template-columns: repeat(auto-fill, minmax(calc(var(--ctl-space-7) * 3.75), 1fr));
  }

  .item{
    position: relative;
    border-radius: var(--ctl-radius-14);
    outline: none;
  }

  .item[aria-disabled="true"]{
    opacity: var(--ctl-opacity-muted);
    cursor: not-allowed;
  }

  .card{
    height: 100%;
    overflow:hidden;
  }

  .media{
    background: color-mix(in srgb, var(--ctl-border) 40%, var(--ctl-bg));
    border-bottom: 1px solid var(--ctl-border);
    position: relative;
  }

  .media::before{
    content:"";
    display:block;
    padding-top: var(--aspect, 56.25%); /* default 16:9 */
  }

  :host([aspect="auto"]) .media::before{ padding-top: 56.25%; }
  :host([aspect="1:1"]) .media::before{ padding-top: 100%; }
  :host([aspect="4:3"]) .media::before{ padding-top: 75%; }
  :host([aspect="16:9"]) .media::before{ padding-top: 56.25%; }
  :host(:not([aspect])) .media::before{ padding-top: 56.25%; }

  img{
    position:absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display:block;
  }

  .body{
    padding: var(--ctl-space-2-5) var(--ctl-space-2-5) var(--ctl-space-3) var(--ctl-space-2-5);
    display:grid;
    gap: var(--ctl-space-1);
  }

  .title{
    font-weight: var(--ctl-font-weight-black);
    letter-spacing: var(--ctl-tracking-tight);
    line-height: var(--ctl-leading-tight);
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sub{
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .badge{
    position:absolute;
    top: var(--ctl-space-2-5);
    right: var(--ctl-space-2-5);
  }

  .selectedRing{
    position:absolute;
    inset: 0;
    border-radius: var(--ctl-radius-14);
    border: var(--ctl-border-width-thick) solid transparent;
    pointer-events:none;
  }

  :host([selectable]) .item[aria-selected="true"] .selectedRing{
    border-color: color-mix(in srgb, var(--ctl-brand) 70%, var(--ctl-color-white));
    box-shadow: 0 0 0 var(--ctl-focus-ring-width) color-mix(in srgb, var(--ctl-brand) 18%, transparent);
  }

  :host([selectable]) .item:focus-visible{
    box-shadow: var(--ctl-focus);
    border-radius: var(--ctl-radius-14);
  }

  .empty{
    padding: var(--ctl-space-3-5);
    border: var(--ctl-border-width-thin) dashed var(--ctl-border);
    border-radius: var(--ctl-radius-14);
    background: color-mix(in srgb, var(--ctl-border) 10%, var(--ctl-bg));
    color: var(--ctl-muted);
    text-align:center;
  }

  /* Lightbox */
  .lbBackdrop{
    position: fixed;
    inset: 0;
    background: var(--ctl-color-overlay-8);
    z-index: var(--ctl-z-modal);
    display:none;
    align-items:center;
    justify-content:center;
    padding: var(--ctl-space-3-5);
  }
  :host([_lb-open]) .lbBackdrop{ display:flex; }

  .lb{
    width: min(980px, calc(100vw - var(--ctl-space-5)));
    max-height: min(720px, calc(100vh - var(--ctl-space-5)));
    border-radius: var(--ctl-radius-14);
    background: var(--ctl-surface);
    border: var(--ctl-border-width-thin) solid color-mix(in srgb, var(--ctl-border) 90%, transparent);
    overflow:hidden;
    box-shadow: var(--ctl-shadow-lg);
    display:grid;
    grid-template-rows: auto 1fr;
  }

  .lbHead{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: var(--ctl-space-2-5);
    padding: var(--ctl-space-2-5);
    border-bottom: var(--ctl-border-width-thin) solid var(--ctl-border);
  }

  .lbTitle{
    min-width:0;
    font-weight: var(--ctl-font-weight-black);
    letter-spacing: var(--ctl-tracking-tight);
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lbBody{
    background: var(--ctl-color-surface-inverse);
    display:grid;
    place-items:center;
    padding: var(--ctl-space-2-5);
  }

  .lbImg{
    max-width: 100%;
    max-height: 100%;
    border-radius: var(--ctl-radius-12);
    box-shadow: var(--ctl-shadow-lg);
  }

  ::slotted(template[slot="item"]){ display:none; }
</style>

<div id="grid" class="grid" part="grid"></div>
<div id="empty" class="empty" part="empty" hidden>
  <slot name="empty">No items.</slot>
</div>

<slot id="itemSlot" name="item"></slot>

<div id="lbBackdrop" class="lbBackdrop" part="lightbox-backdrop" aria-hidden="true">
  <div class="lb" part="lightbox" role="dialog" aria-modal="true" aria-label="Image preview">
    <div class="lbHead" part="lightbox-header">
      <div class="lbTitle" id="lbTitle"></div>
      <ctl-button size="sm" variant="secondary" id="lbClose">Close</ctl-button>
    </div>
    <div class="lbBody" part="lightbox-body">
      <img id="lbImg" class="lbImg" alt="" />
    </div>
  </div>
</div>
`;

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}
function toArray(v){ return Array.isArray(v) ? v : []; }
function esc(s){
  return String(s ?? "")
    .replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;")
    .replaceAll('"',"&quot;").replaceAll("'","&#039;");
}

function normalizeGap(g){
  const v = String(g || "md");
  return ["xs","sm","md","lg","xl"].includes(v) ? v : "md";
}

function gapPx(g){
  return ({ xs: 6, sm: 10, md: 12, lg: 16, xl: 22 })[g] || 12;
}

function normalizeAspect(a){
  const v = String(a || "auto");
  return ["auto","1:1","4:3","16:9"].includes(v) ? v : "auto";
}

export class CtlGallery extends HTMLElement {
  static get observedAttributes() {
    return ["columns","gap","aspect","selectable","multiple","value","values","lightbox","disabled"];
  }

  constructor(){
    super();
    this.attachShadow({ mode:"open" }).appendChild(template.content.cloneNode(true));

    this._grid = this.shadowRoot.getElementById("grid");
    this._empty = this.shadowRoot.getElementById("empty");
    this._itemSlot = this.shadowRoot.getElementById("itemSlot");

    this._lbBackdrop = this.shadowRoot.getElementById("lbBackdrop");
    this._lbClose = this.shadowRoot.getElementById("lbClose");
    this._lbTitle = this.shadowRoot.getElementById("lbTitle");
    this._lbImg = this.shadowRoot.getElementById("lbImg");

    this._items = [];
    this._selected = new Set();
    this._focused = null;
    this._itemTemplate = null;

    this._lbItem = null;

    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("columns")) this.setAttribute("columns", "auto");
    if (!this.hasAttribute("gap")) this.setAttribute("gap", "md");
    if (!this.hasAttribute("aspect")) this.setAttribute("aspect", "auto");

    // hydrate selection from attrs
    this._hydrateSelection();

    this._grid.addEventListener("click", this._onClick);
    this._grid.addEventListener("keydown", this._onKeyDown);
    this._itemSlot.addEventListener("slotchange", this._onSlotChange);

    this._lbClose.addEventListener("click", () => this._closeLightbox());
    this._lbBackdrop.addEventListener("click", this._onBackdropClick);

    this._collectTemplate();
    this._render();
  }

  disconnectedCallback(){
    this._grid.removeEventListener("click", this._onClick);
    this._grid.removeEventListener("keydown", this._onKeyDown);
    this._itemSlot.removeEventListener("slotchange", this._onSlotChange);

    this._lbClose.removeEventListener("click", () => this._closeLightbox());
    this._lbBackdrop.removeEventListener("click", this._onBackdropClick);
  }

  attributeChangedCallback(name){
    if (name === "gap") this._applyGap();
    if (name === "aspect") this._applyAspect();
    if (name === "value" || name === "values" || name === "multiple") {
      this._hydrateSelection();
    }
    this._render();
  }

  // ---- attributes / properties ----
  get selectable(){ return this.hasAttribute("selectable"); }
  set selectable(v){ v ? this.setAttribute("selectable","") : this.removeAttribute("selectable"); }

  get multiple(){ return this.hasAttribute("multiple"); }
  set multiple(v){ v ? this.setAttribute("multiple","") : this.removeAttribute("multiple"); }

  get lightbox(){ return this.hasAttribute("lightbox"); }
  set lightbox(v){ v ? this.setAttribute("lightbox","") : this.removeAttribute("lightbox"); }

  get value(){ return this.getAttribute("value") || ""; }
  set value(v){ v ? this.setAttribute("value", String(v)) : this.removeAttribute("value"); }

  get values(){
    const raw = this.getAttribute("values");
    return raw ? toArray(safeJsonParse(raw, [])).map(String) : [];
  }
  set values(v){
    const arr = toArray(v).map(String);
    this.setAttribute("values", JSON.stringify(arr));
  }

  get items(){ return this._items; }
  set items(v){
    this._items = toArray(v).map((x) => this._normalizeItem(x)).filter((x) => x.id);
    if (!this._focused && this._items[0]) this._focused = this._items[0].id;
    this._render();
  }

  _normalizeItem(x){
    const o = (x && typeof x === "object") ? x : {};
    return {
      id: String(o.id || ""),
      src: String(o.src || ""),
      alt: o.alt == null ? "" : String(o.alt),
      title: o.title == null ? "" : String(o.title),
      subtitle: o.subtitle == null ? "" : String(o.subtitle),
      href: o.href == null ? "" : String(o.href),
      disabled: !!o.disabled,
      meta: o.meta == null ? "" : String(o.meta)
    };
  }

  // ---- template override ----
  _onSlotChange(){
    this._collectTemplate();
    this._render();
  }

  _collectTemplate(){
    const assigned = this._itemSlot.assignedElements({ flatten:true }) || [];
    this._itemTemplate = assigned.find((el) => el.tagName === "TEMPLATE") || null;
  }

  _renderTemplate(tmpl, data){
    return tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      return esc(v == null ? "" : String(v));
    });
  }

  // ---- selection ----
  _hydrateSelection(){
    if (this.multiple) {
      this._selected = new Set(this.values.map(String));
    } else {
      const v = this.value;
      this._selected = new Set(v ? [String(v)] : []);
    }
  }

  _select(id){
    if (!this.selectable) return;

    const item = this._items.find((x) => x.id === id);
    if (!item || item.disabled) return;

    let selected = false;

    if (this.multiple) {
      if (this._selected.has(id)) {
        this._selected.delete(id);
        selected = false;
      } else {
        this._selected.add(id);
        selected = true;
      }
      this.values = Array.from(this._selected);
    } else {
      this._selected = new Set([id]);
      this.value = id;
      selected = true;
    }

    this.dispatchEvent(new CustomEvent("ctl-select", {
      bubbles: true,
      composed: true,
      detail: {
        id,
        item,
        selected,
        values: this.multiple ? Array.from(this._selected) : []
      }
    }));

    this._render();
  }

  // ---- lightbox ----
  _openLightbox(item){
    if (!this.lightbox) return;
    this._lbItem = item;
    this.setAttribute("_lb-open", "");
    this._lbBackdrop.setAttribute("aria-hidden", "false");
    this._lbTitle.textContent = item.title || item.alt || "Preview";
    this._lbImg.src = item.src;
    this._lbImg.alt = item.alt || "";
    this.dispatchEvent(new CustomEvent("ctl-open", {
      bubbles: true,
      composed: true,
      detail: { id: item.id, item }
    }));
    queueMicrotask(() => this._lbClose?.focus());
  }

  _closeLightbox(){
    if (!this.hasAttribute("_lb-open")) return;
    this.removeAttribute("_lb-open");
    this._lbBackdrop.setAttribute("aria-hidden", "true");
    const prev = this._focused ? this._itemEl(this._focused) : null;
    queueMicrotask(() => prev?.focus?.());
  }

  _onBackdropClick(e){
    // close when clicking outside dialog
    if (e.target === this._lbBackdrop) this._closeLightbox();
  }

  // ---- render ----
  _applyGap(){
    const g = normalizeGap(this.getAttribute("gap"));
    this.style.setProperty("--gap", `${gapPx(g)}px`);
  }

  _applyAspect(){
    const a = normalizeAspect(this.getAttribute("aspect"));
    // convert to padding percent for media placeholder
    const pct = a === "1:1" ? "100%" : a === "4:3" ? "75%" : "56.25%";
    this.style.setProperty("--aspect", pct);
  }

  _itemEl(id){
    return id ? this.shadowRoot.querySelector(`.item[data-id="${CSS.escape(id)}"]`) : null;
  }

  _render(){
    this._applyGap();
    this._applyAspect();

    const items = this._items || [];
    const empty = items.length === 0;

    this._empty.hidden = !empty;
    this._grid.style.display = empty ? "none" : "grid";
    if (empty) {
      this._grid.innerHTML = "";
      return;
    }

    if (!this._focused) this._focused = items[0].id;

    const html = items.map((it) => {
      const selected = this.selectable && this._selected.has(it.id);
      const disabled = !!it.disabled || this.hasAttribute("disabled");

      const data = {
        id: it.id,
        src: it.src,
        alt: it.alt,
        title: it.title,
        subtitle: it.subtitle,
        meta: it.meta,
        selected: selected ? "true" : "false",
        disabled: disabled ? "true" : "false",
        href: it.href
      };

      const inner = this._itemTemplate
        ? this._renderTemplate(this._itemTemplate, data)
        : `
          <ctl-card class="card" part="card">
            <div class="media" part="media">
              ${it.src ? `<img src="${esc(it.src)}" alt="${esc(it.alt || "")}" loading="lazy" />` : ``}
              ${selected ? `<div class="badge" part="badge"><ctl-badge pill intent="info">Selected</ctl-badge></div>` : ``}
            </div>
            <div class="body" part="body">
              <div class="title">${esc(it.title || it.alt || it.id)}</div>
              ${it.subtitle ? `<div class="sub">${esc(it.subtitle)}</div>` : ``}
            </div>
          </ctl-card>
        `;

      return `
        <div class="item"
             part="item"
             data-id="${esc(it.id)}"
             tabindex="${it.id === this._focused ? "0" : "-1"}"
             role="${this.selectable ? "option" : "button"}"
             aria-selected="${selected ? "true" : "false"}"
             aria-disabled="${disabled ? "true" : "false"}">
          ${inner}
          <div class="selectedRing" part="selected-ring"></div>
        </div>
      `;
    }).join("");

    this._grid.innerHTML = html;
  }

  // ---- interaction ----
  _onClick(e){
    const path = e.composedPath ? e.composedPath() : [];
    const itemEl = path.find((n) => n?.classList?.contains?.("item") && n?.dataset?.id);
    if (!itemEl) return;

    const id = itemEl.dataset.id;
    const item = this._items.find((x) => x.id === id);
    if (!item || item.disabled || this.hasAttribute("disabled")) return;

    this._focused = id;
    this._syncRoving();

    if (this.selectable) {
      this._select(id);
      return;
    }

    if (this.lightbox) {
      this._openLightbox(item);
      return;
    }

    if (item.href) {
      this.dispatchEvent(new CustomEvent("ctl-navigate", {
        bubbles: true,
        composed: true,
        detail: { id, href: item.href, item }
      }));
    }
  }

  _syncRoving(){
    const prev = this.shadowRoot.querySelector(`.item[tabindex="0"]`);
    if (prev && prev.dataset.id !== this._focused) prev.tabIndex = -1;
    const next = this._itemEl(this._focused);
    if (next) next.tabIndex = 0;
  }

  _onKeyDown(e){
    if (this.hasAttribute("_lb-open")) {
      if (e.key === "Escape") { e.preventDefault(); this._closeLightbox(); }
      return;
    }

    const items = this._items || [];
    if (!items.length) return;

    const currentIndex = items.findIndex((x) => x.id === this._focused);
    if (currentIndex < 0) return;

    // Estimate columns based on computed style
    const style = getComputedStyle(this._grid);
    const cols = (style.gridTemplateColumns || "").split(" ").filter(Boolean).length || 1;

    let nextIndex = currentIndex;

    if (e.key === "ArrowRight") nextIndex = Math.min(items.length - 1, currentIndex + 1);
    if (e.key === "ArrowLeft") nextIndex = Math.max(0, currentIndex - 1);
    if (e.key === "ArrowDown") nextIndex = Math.min(items.length - 1, currentIndex + cols);
    if (e.key === "ArrowUp") nextIndex = Math.max(0, currentIndex - cols);
    if (e.key === "Home") nextIndex = 0;
    if (e.key === "End") nextIndex = items.length - 1;

    if (nextIndex !== currentIndex) {
      e.preventDefault();
      this._focused = items[nextIndex].id;
      this._syncRoving();
      this._itemEl(this._focused)?.focus?.();
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const it = items[currentIndex];
      if (this.selectable) this._select(it.id);
      else if (this.lightbox) this._openLightbox(it);
      else if (it.href) {
        this.dispatchEvent(new CustomEvent("ctl-navigate", {
          bubbles: true,
          composed: true,
          detail: { id: it.id, href: it.href, item: it }
        }));
      }
    }

    if (e.key === "Escape" && this.open) {
      // noop; kept for parity with other components
    }
  }
}

customElements.define("ctl-gallery", CtlGallery);
